import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { ArrowLeft } from '../../icons';
import { useDevice } from '../../providers/Device';
import { getMobileTheme, ScopedThemeProvider, useTheme } from '../../providers/Theme';
import { CloseButton } from '../CloseButton';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Divider } from '../Divider';
import { IsInModalProvider, useModalHost } from '../OverlayContext/ModalContainer/ModalContainer';
import { Typography } from '../Typography';
import styles from './FullPageModal.module.scss';
const HeaderStartContext = /*#__PURE__*/ React.createContext(null);
const HeaderEndContext = /*#__PURE__*/ React.createContext(null);
export function FullPageModal(props) {
    const { children, className, contentClassName, headerClassName, contentSkin = 'default', titleVariant = 'h5', testId, onClose, hideHeader, hideCloseBtn, usePrevIcon, closeBiName, scrollContainerRef } = props, rest = _object_without_properties(props, [
        "children",
        "className",
        "contentClassName",
        "headerClassName",
        "contentSkin",
        "titleVariant",
        "testId",
        "onClose",
        "hideHeader",
        "hideCloseBtn",
        "usePrevIcon",
        "closeBiName",
        "scrollContainerRef"
    ]);
    const { isMobileDevice } = useDevice();
    const parentTheme = useTheme();
    const [headerStartEl, setHeaderStartEl] = useState(null);
    const [headerEndEl, setHeaderEndEl] = useState(null);
    const closeBtn = /*#__PURE__*/ _jsx(CloseButton, {
        // WHY: 'close' stays the default because dismissal is this button's inherent semantic; closeBiName exists only for a surface that reuses the slot for another action (usePrevIcon), which the consumer alone can name.
        bi: {
            name: closeBiName !== null && closeBiName !== void 0 ? closeBiName : 'close'
        },
        skin: "transparent",
        icon: usePrevIcon ? /*#__PURE__*/ _jsx(ArrowLeft, {
            size: 16
        }) : undefined,
        testId: testId && testId + '-close-btn',
        onClick: onClose
    });
    const wrapWithProviders = (children)=>/*#__PURE__*/ _jsx(HeaderStartContext.Provider, {
            value: headerStartEl,
            children: /*#__PURE__*/ _jsx(HeaderEndContext.Provider, {
                value: headerEndEl,
                children: children
            })
        });
    const wrapWithMobileTheme = (content)=>/*#__PURE__*/ _jsx(ScopedThemeProvider, {
            theme: _object_spread({}, parentTheme, getMobileTheme()),
            testId: testId && "".concat(testId, "-content-theme"),
            children: content
        });
    const modalHost = useModalHost();
    useEffect(()=>{
        modalHost.setOwnsFullScreen(true);
        return ()=>modalHost.setOwnsFullScreen(false);
    }, [
        modalHost
    ]);
    return /*#__PURE__*/ _jsx(IsInModalProvider, {
        value: false,
        children: /*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({}, rest), {
            className: cn(styles.root, className, {
                [styles.mobile]: isMobileDevice
            }),
            "data-testid": testId,
            children: [
                !hideHeader && /*#__PURE__*/ _jsxs("div", {
                    className: cn(styles.header, headerClassName),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.flexContainer,
                            children: [
                                !hideCloseBtn && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        closeBtn,
                                        /*#__PURE__*/ _jsx(Divider, {
                                            orientation: "vertical",
                                            margin: 8,
                                            size: 32
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: titleVariant,
                                    weight: "normal",
                                    noWrap: isMobileDevice,
                                    className: cn(styles.flexContainer, styles.headerTitle, {
                                        [styles.headerTitleMobile]: isMobileDevice,
                                        [styles.headerTitleMobileH6]: isMobileDevice && titleVariant === 'h6'
                                    }),
                                    ref: setHeaderStartEl,
                                    testId: testId && testId + '-header-start'
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.flexContainer, styles.headerEnd),
                            children: /*#__PURE__*/ _jsx("div", {
                                ref: setHeaderEndEl,
                                className: styles.flexContainer,
                                "data-testid": testId && "".concat(testId, "-header-end")
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.content, styles["".concat(contentSkin, "Skin")], contentClassName),
                    ref: scrollContainerRef,
                    "data-testid": testId && "".concat(testId, "-content"),
                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                        condition: isMobileDevice,
                        wrapper: wrapWithMobileTheme,
                        children: wrapWithProviders(children)
                    })
                })
            ]
        }))
    });
}
const useSlot = (context, children)=>{
    const element = useContext(context);
    return element ? /*#__PURE__*/ createPortal(children, element) : null;
};
export const FullPageModalHeaderStart = (p)=>useSlot(HeaderStartContext, p.children);
export const FullPageModalHeaderEnd = (p)=>useSlot(HeaderEndContext, p.children);
export function FullPageModalMainActions(props) {
    return useSlot(HeaderEndContext, /*#__PURE__*/ _jsx("div", {
        className: styles.flexContainer,
        children: props.children
    }));
}
FullPageModal.HeaderStart = FullPageModalHeaderStart;
FullPageModal.HeaderEnd = FullPageModalHeaderEnd;
FullPageModal.MainActions = FullPageModalMainActions;
